/* eslint-disable max-len */
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseURL } from "../config"
import { authHeader } from "../CommonMethods/Auth-Headers";
import { userService } from "../services/user.services";

async function myCustomBaseQuery(args, api, extraOptions) {

    // Call the default fetchBaseQuery
    const rawBaseQuery = fetchBaseQuery({
        baseUrl: baseURL,
        prepareHeaders: async (headers) => {
            const header = await authHeader();
            headers.set('authorization', header.Authorization);
            return headers;
        },
    });

    const result = await rawBaseQuery(args, api, extraOptions);    
    // Check for error code 400 and specific message
    if (
        result?.error?.status === 400 &&
        result?.error?.data?.message === "Access denied: User account is inactive"
    ) {
        try {
            // Store custom status
            sessionStorage.setItem("custom-status", "inactive");
            const browserIdValue = localStorage.getItem("arcm_trust_browser_token");

            // Perform logout
            await userService.logout();

            // Clear session storage and retain the browser token
            sessionStorage.clear();
            sessionStorage.setItem("custom-status", "inactive");
            localStorage.setItem("arcm_trust_browser_token", browserIdValue);

            // Redirect to the login page or home page
            setTimeout(() => {
                window.location.href = "/";
            }, 500);

            // Return early to stop further processing of the result
            return {
                error: {
                    status: 400,
                    message: "Logged out due to inactive account.",
                },
            };
        } catch (error) {
            console.error("Logout failed:", error);
        }
    }

    // Return the result for normal flow
    return result;
}


export const organizationusersList = createApi({
    reducerPath: 'organizationusersList',
    baseQuery: myCustomBaseQuery,
    tagTypes: ['OrgUser', 'SubDiv', 'SelectedOrgUserOrg'],
    endpoints: (builder) => ({
        // get organization list api call 
        getOrganizationUser: builder.query({
            query: (searchParams) => `organization-users/user-list?${searchParams}`,
            providesTags: (result = [], error, arg) => error === undefined ? [
                'OrgUser',
                ...result?.organizationUsers !== undefined && Array.isArray(result?.organizationUsers) && result?.organizationUsers?.map(({ _id }) => ({ type: 'OrgUser', id: _id }))
            ] : [],

        }),

        // add organization api calling
        addOrganization: builder.mutation({
            query: org => ({
                url: `organization-users`,
                method: 'POST',
                body: org
            }),
            invalidatesTags: ['OrgUser', 'SubDiv']
        }),

        // get subdivision name and id while adding organization
        getSubDiv: builder.query({
            query: (searchParams) => `organization-users/list-users-subdivisions?${searchParams}`,
            providesTags: (result = [], error, arg) => error === undefined ? [

                'SubDiv',
                ...result?.data !== undefined && Array.isArray(result?.data) && result?.data?.map(({ _id }) => ({ type: 'SubDiv', id: _id }))
            ] : []
        }),
        // view organization api call
        viewOrganizationUser: builder.query({
            query: (id) => `organization-users/${id}`,
            providesTags: (result, error, arg) => [{ type: 'OrgUser', id: arg?.split('?')[0] }]
        }),
        // websocket view org api
        viewWebsocketOrganizationUser:builder.query({
            query:(id)=>`organization-users/${id}`,
            providesTags: (result, error, arg) => [{ type: 'OrgUser', id: arg?.split('?')[0]  }]
        }),

        // export subdivision api call
        exportSubdivision: builder.mutation({
            query: ({ queryString, columns }) => ({
                url: `organization-users/export-subdivisions-list?${queryString}`,
                method: 'POST',
                body: { "columns": columns, "timezone": Intl.DateTimeFormat().resolvedOptions().timeZone }
            }),
        }),

        // edit organization api call
        editOrgUser: builder.mutation({
            query: ({ id, body }) => ({
                url: id !== undefined ? `/organization-users/${id}` : `/organization-users/`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: ['OrgUser']
        }),

        // get subdivision list api call in edit page 
        getOrgUserSubdivisions: builder.query({
            query: (searchParams) => `organization-users/list-subdivisions?${searchParams}`,
            providesTags: (result = [], error, arg) => error === undefined ? [
                'SelectedOrgUserOrg',
                ...result?.data !== undefined && Array.isArray(result?.data) && result?.data?.map(({ _id }) => ({ type: 'SelectedOrg', id: _id }))
            ] : [],
        }),

        // delete otganization api call
        deleteOrgUserAssociatedOrganization: builder.mutation({
            query: ({ userId, subdivision_id }) => {
                return {
                    url: `organization-users/unlink-subdivisions/${userId}?subdivision_id=${subdivision_id}`,
                    method: 'DELETE',
                };
            },
            invalidatesTags: ['SelectedOrgUserOrg'],
        }),

        // link organization api call in edit page
        linkOrgUsertoOrganization: builder.mutation({
            query: ({ userId, body }) => ({
                url: `organization-users/link-subdivisions/${userId}`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['SelectedOrgUserOrg']
        }),

        // export organization api call
        exportOrganizationUsers: builder.mutation({
            query: ({ queryString, columns, dateformat }) => ({
                url: `organization-users/export-user?${queryString}`,
                method: 'POST',
                body: { "columns": columns, "dateformat": dateformat, "timezone": Intl.DateTimeFormat().resolvedOptions().timeZone }
            }),
        }),
        updateEmail: builder.mutation({
            query: ({ id, body }) => ({
                url: `users/update-email-address/${id}`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['History']
        }),
        emailVerified: builder.mutation({
            query: ({ body }) => ({
                url: `users/verify-email-address`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['History']
        }),
        // activity log organization user
        getOrgUserActivityHistory: builder.query({
            query: (searchParams) => `organization-users/list-changes?${searchParams}`,
            providesTags: ['OrgUserActivityHistory']

        }),
        // export activity log
        exportOrgUserActivityHistory:builder.mutation({
            query:({sort,columns,username,dateformat ,params})=>{
                const queryParams = Object.entries(params)
                    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
                    .join('&');
                return{
                    url:`organization-users/export-profile-changes?${queryParams}`,
                    method:'POST',
                    body:{"columns":columns,"target_user_name":username,"dateformat":dateformat, "timezone":Intl.DateTimeFormat().resolvedOptions().timeZone }
                };
            },
        }),
    }),


})

export const { useGetOrgUserActivityHistoryQuery,   useExportOrgUserActivityHistoryMutation, useLazyGetUsersQuery, useExportOrganizationUsersMutation, useGetOrganizationUserQuery, useLazyViewOrganizationUserQuery, useLazyGetOrgUserSubdivisionsQuery, useEditOrgUserMutation, useExportSubdivisionMutation, useViewOrganizationUserQuery, useGetSubDivQuery, useAddOrganizationMutation, useDeleteOrgUserAssociatedOrganizationMutation, useLinkOrgUsertoOrganizationMutation, useUpdateEmailMutation,
    useEmailVerifiedMutation, useLazyViewWebsocketOrganizationUserQuery} = organizationusersList